import api from "@/lib/axiosConfig";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { baseRoute as tirageBaseRoute } from "./tirage.api";

const baseRoute = "back/api/tirages/history";

const _tirageHistorySchema = z.object({
  id: z.string(),
  tirageHistoriqueHistoriqueNom: z.string(),
  tirageHistoriqueHistoriqueVille: z.string(),
  tirageHistoriqueHistoriqueDates: z.string(),
  tirageHistoriqueHistoriqueCollectionParticuliere: z.boolean(),
  tirageId: z.string(),
  id_imported: z.number().optional(),
  provenanceRank: z.number().optional(),
  updatedAt: z.string().optional(),
  createdAt: z.string().optional(),
  updatedById: z.string().optional(),
  createdById: z.string().optional(),
});

export type TirageHistory = z.infer<typeof _tirageHistorySchema>;

export type TirageHistoryMergedAuction = {
  kind: "auction";
  id: string;
  tirageId: string | null;
  provenanceRank?: number;
  tirageDansVentesDescriptionNumeroLot?: string;
  auction: {
    ventesDescriptionCommissaires?: string;
    ventesDescriptionLieu?: string;
    ventesDescriptionVille?: string;
    ventesDescriptionPays?: string;
    ventesDescriptionDate?: string;
  } | null;
};

export type TirageHistoryMerged = {
  timeline: Array<
    (TirageHistory & { kind: "history" }) | TirageHistoryMergedAuction
  >;
};

const historyQueryKey = (tirageId: string | undefined, includeAuctions: boolean) => [
  "tirage",
  tirageId,
  "history",
  includeAuctions ? "withAuctions" : "only",
];

async function getHistoryOfTirage(tirageId: string) {
  const { data } = await api.get<TirageHistory[]>(
    `${tirageBaseRoute}/${tirageId}/history`,
  );
  return data;
}

async function getHistoryOfTirageMerged(tirageId: string) {
  const { data } = await api.get<TirageHistoryMerged>(
    `${tirageBaseRoute}/${tirageId}/history`,
    { params: { includeAuctions: "true" } },
  );
  return data;
}

export function useGetHistoryOfTirage(tirageId: string | undefined) {
  return useQuery({
    queryKey: historyQueryKey(tirageId, false),
    queryFn: () => (tirageId ? getHistoryOfTirage(tirageId) : Promise.resolve([])),
    enabled: !!tirageId,
  });
}

export function useGetHistoryOfTirageMerged(
  tirageId: string | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: historyQueryKey(tirageId, true),
    queryFn: () =>
      tirageId
        ? getHistoryOfTirageMerged(tirageId)
        : Promise.resolve({ timeline: [] } as TirageHistoryMerged),
    enabled: !!tirageId && enabled,
  });
}

const _createTirageHistorySchema = _tirageHistorySchema.omit({
  id: true,
  tirageId: true,
  createdAt: true,
  createdById: true,
  updatedAt: true,
  updatedById: true,
});

export type CreateTirageHistory = z.infer<typeof _createTirageHistorySchema>;

async function createTirageHistory({
  history,
  tirageId,
}: {
  history: CreateTirageHistory;
  tirageId: string;
}) {
  const { data } = await api.post(
    `${tirageBaseRoute}/${tirageId}/history`,
    history,
  );
  return data;
}

export function useCreateTirageHistory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createTirageHistory,
    onSuccess: (_, { tirageId }) => {
      queryClient.invalidateQueries({
        queryKey: ["tirage", tirageId, "history"],
      });
    },
  });
}

export type EditTirageHistory = CreateTirageHistory;

async function editTirageHistory({
  history,
  historyId,
}: {
  history: EditTirageHistory;
  historyId: string;
  tirageId: string;
}) {
  const { data } = await api.patch(`${baseRoute}/${historyId}`, history);
  return data;
}

export function useEditTirageHistory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: editTirageHistory,
    onSuccess: (_, { tirageId, historyId }) => {
      queryClient.invalidateQueries({
        queryKey: ["tirage-history", historyId],
      });
      queryClient.invalidateQueries({
        queryKey: ["tirage", tirageId, "history"],
      });
    },
  });
}

async function deleteTirageHistory({
  historyId,
}: {
  historyId: string;
  tirageId: string;
}) {
  await api.delete(`${baseRoute}/${historyId}`);
}

export function useDeleteTirageHistory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteTirageHistory,
    onSuccess: (_, { tirageId, historyId }) => {
      queryClient.invalidateQueries({
        queryKey: ["tirage-history", historyId],
      });
      queryClient.invalidateQueries({
        queryKey: ["tirage", tirageId, "history"],
      });
    },
  });
}

async function updateTirageHistorySequence({
  newSequence,
  tirageId,
}: {
  newSequence: string[];
  tirageId: string;
}) {
  await api.patch(`${tirageBaseRoute}/${tirageId}/history`, {
    sequence: newSequence,
  });
}

export const useUpdateTirageHistorySequence = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateTirageHistorySequence,
    onSuccess: (_, { tirageId }) => {
      queryClient.invalidateQueries({
        queryKey: ["tirage", tirageId, "history"],
      });
    },
  });
};

async function reorderTirageProvenance({
  tirageId,
  timeline,
}: {
  tirageId: string;
  timeline: { kind: "history" | "auction"; id: string }[];
}) {
  await api.patch(`${tirageBaseRoute}/${tirageId}/provenance-sequence`, {
    timeline,
  });
}

export function useReorderTirageProvenance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: reorderTirageProvenance,
    onSuccess: (_, { tirageId }) => {
      queryClient.invalidateQueries({
        queryKey: ["tirage", tirageId, "history"],
      });
    },
  });
}
